import { type FC, useState } from 'react'
import classNames from 'classnames'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { type EmailTemplate, EmailTemplatePurpose } from '../../../../models/Scheduling'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { useSchedulingEmailTemplates } from '../../../Scheduling/shared/useSchedulingEmailTemplates'
import { SettingsCard, SettingsSection } from './SettingsSection'
import { ADVISOR_EMAIL_PURPOSES, CLIENT_EMAIL_PURPOSES, type EmailTemplatePurposeInfo } from './emailTemplatePurposes'
import { tokenizeTemplateText, transformTemplateBodyHtml } from './emailTemplatePlaceholders'

const CHIP_CLASS = 'zocks-px-0.25 zocks-bg-color-primary-light rounded-1'

interface TemplateTextProps {
  text: string
}

const TemplateText: FC<TemplateTextProps> = ({ text }) => (
  <>
    {tokenizeTemplateText(text).map((token, index) =>
      token.type === 'chip' ? (
        <span key={index} className={CHIP_CLASS}>
          {token.value}
        </span>
      ) : (
        <span key={index}>{token.value}</span>
      ),
    )}
  </>
)

interface TemplateCardProps {
  info: EmailTemplatePurposeInfo
  template?: EmailTemplate
  isExpanded: boolean
  onToggle: () => void
}

const TemplateCard: FC<TemplateCardProps> = ({ info, template, isExpanded, onToggle }) => {
  const regionId = `email-template-${info.purpose}`
  const labelId = `${regionId}-label`

  return (
    <SettingsCard>
      <button
        type='button'
        className='d-flex align-items-center justify-content-between gap-3 py-3 zocks-px-1.25 border-0 bg-transparent text-start w-100'
        aria-expanded={isExpanded}
        aria-controls={regionId}
        onClick={onToggle}
      >
        <span className='d-flex flex-column'>
          <span id={labelId} className='bold'>
            {info.label}
          </span>
          <span className='paragraph-small zocks-text-gray-600'>{info.description}</span>
        </span>
        <i aria-hidden='true' className={classNames('bi zocks-text-gray-500', isExpanded ? 'bi-chevron-up' : 'bi-chevron-down')} />
      </button>
      {isExpanded ? (
        template != null ? (
          <div id={regionId} role='region' aria-labelledby={labelId} className='d-flex flex-column gap-3 px-3 pb-3'>
            <div className='d-flex flex-column zocks-gap-0.5 p-3 rounded-2 zocks-bg-color-gray-100 paragraph-small'>
              <div className='d-flex align-items-baseline gap-2'>
                <span className='lead-small zocks-text-gray-500 text-uppercase'>From</span>
                <span>
                  <span className={CHIP_CLASS}>Advisor full name</span> via Zocks · notifications@booking.zocks.com
                </span>
              </div>
              <div className='d-flex align-items-baseline gap-2'>
                <span className='lead-small zocks-text-gray-500 text-uppercase'>Subject</span>
                <span>
                  <TemplateText text={template.subject} />
                </span>
              </div>
            </div>
            <div className='pe-none' dangerouslySetInnerHTML={{ __html: transformTemplateBodyHtml(template.body, CHIP_CLASS) }} />
          </div>
        ) : (
          <div id={regionId} role='region' aria-labelledby={labelId} className='px-3 pb-3 paragraph-small zocks-text-gray-600'>
            This template isn&apos;t available yet.
          </div>
        )
      ) : null}
    </SettingsCard>
  )
}

interface TemplateGroupProps {
  title: string
  purposes: EmailTemplatePurposeInfo[]
  byPurpose: (purpose: EmailTemplatePurpose) => EmailTemplate | undefined
  expandedPurpose: EmailTemplatePurpose | null
  onToggle: (purpose: EmailTemplatePurpose) => void
}

const TemplateGroup: FC<TemplateGroupProps> = ({ title, purposes, byPurpose, expandedPurpose, onToggle }) => (
  <div className='d-flex flex-column zocks-gap-0.75'>
    <h6 className='mb-0 zocks-text-gray-900'>{title}</h6>
    <div className='d-flex flex-column gap-2'>
      {purposes.map((info) => (
        <TemplateCard
          key={info.purpose}
          info={info}
          template={byPurpose(info.purpose)}
          isExpanded={expandedPurpose === info.purpose}
          onToggle={() => onToggle(info.purpose)}
        />
      ))}
    </div>
  </div>
)

export const EmailTemplatesSection: FC = () => {
  const { isLoading, isError, byPurpose } = useSchedulingEmailTemplates()
  const { hasQuota } = useZocksQuota()
  const [expandedPurpose, setExpandedPurpose] = useState<EmailTemplatePurpose | null>(null)

  const clientPurposes = hasQuota('scheduling.reminders.value')
    ? CLIENT_EMAIL_PURPOSES
    : CLIENT_EMAIL_PURPOSES.filter((info) => info.purpose !== EmailTemplatePurpose.MEETING_REMINDER)

  const handleToggle = (purpose: EmailTemplatePurpose) => {
    setExpandedPurpose((current) => (current === purpose ? null : purpose))
  }

  return (
    <SettingsSection
      title='Email templates'
      titleAdornment={
        <InfoTooltip title='Contact your implementation manager if you want to customize these templates in your account.' label='About email templates' />
      }
      subtitle='The scheduling emails Zocks sends for you, with details filled in automatically. Booking and reschedule confirmations come from your calendar, not from here.'
    >
      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : isError ? (
        <div className='paragraph-small zocks-text-gray-600'>Could not load the email templates. Please try again.</div>
      ) : (
        <>
          <TemplateGroup
            title='Sent to clients on your behalf'
            purposes={clientPurposes}
            byPurpose={byPurpose}
            expandedPurpose={expandedPurpose}
            onToggle={handleToggle}
          />
          <TemplateGroup
            title='Sent to you'
            purposes={ADVISOR_EMAIL_PURPOSES}
            byPurpose={byPurpose}
            expandedPurpose={expandedPurpose}
            onToggle={handleToggle}
          />
        </>
      )}
    </SettingsSection>
  )
}
